import queryString from "query-string";
import dayjs from "dayjs";
import useCurrentUserStore from "../store/userStore";
import { fetchUserData } from "./graphql/users";

function getToken() {
    return localStorage.getItem("xcube-session-key");
}

const defaultOptions = {
    method: "GET",
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    redirect: "follow",
    referrerPolicy: "no-referrer",
};

const HOST = import.meta.env.VITE_APP_API_URL;

const filterToParams = (filters, { forStats = false } = {}) => {
    const startDate = filters.startDate || new Date(0);
    const endDate = filters.endDate || new Date(2999, 0, 1);

    let deleted = undefined;
    if (filters.options.includes("Only deleted")) {
        deleted = true;
    } else if (!filters.options.includes("Include deleted")) {
        deleted = false;
    }

    const newFilters = {
        deleted,
        date_lte: dayjs(endDate).endOf("day").toISOString(),
        date_gt: dayjs(startDate).startOf("day").toISOString(),
        demo: false,
    };
    if (!forStats) {
        newFilters.limit = filters.limit;
        newFilters._sort = "date:desc";
    }
    if (filters.options.includes("Demo")) {
        newFilters.demo = true;
    }
    if (filters.locationId !== "all") {
        newFilters.location_id = filters.locationId;
    }
    if (filters.experienceId !== "all") {
        newFilters.experience_id = filters.experienceId;
    }
    if (filters.options.includes("Not Finished")) {
        newFilters.finished = false;
    }
    if (filters.options.includes("Consent To Share")) {
        newFilters.consent_to_share = true;
    }
    if (filters.options.includes("Refund")) {
        newFilters.refund = true;
    }
    if (filters.options.includes("Voucher")) {
        newFilters.voucher = true;
    }
    if (filters.xCubeId !== "all") {
        newFilters.xcube_id = filters.xCubeId;
    }
    return newFilters;
}

export const getBookingTable = async (filters) => {
    const params = filterToParams(filters);
    const path = `/bookings/table?${params ? queryString.stringify(params) : ""}`;
    const options = getOptions();
    const res = await request(path, options);
    const data = await res.json();
    if (data.statusCode >= 400) {
        throw new Error(data.message);
    }
    return data;
};

export const getBookingStats = async (filters, { subdivision, statistic }) => {
    const params = {
        ...filterToParams(filters, { forStats: true }),
        subdivision,
        statistic,
    };
    const path = `/bookings/stats?${queryString.stringify(params)}`;
    const options = getOptions();
    const res = await request(path, options);
    const data = await res.json();
    if (data.statusCode >= 400) {
        throw new Error(data.message);
    }
    return data;
};

/** Experience / duration pricing rows from Strapi `prices` (paginated).
 * Uses fetch directly — a missing price permission must not force logout (unlike `request`).
 */
export const getPrices = async () => {
    let result = [];
    let offset = 0;
    let page = [];
    do {
        const path = `/prices?${queryString.stringify({
            _limit: 100,
            _start: offset,
            _sort: "duration:asc",
        })}`;
        const res = await fetch(HOST + path, getOptions());
        if (res.status === 401 || res.status === 403) {
            throw new Error("Pricing is not available for this role yet.");
        }
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || `Could not load prices (${res.status}).`);
        }
        page = await res.json();
        if (!Array.isArray(page)) {
            throw new Error("Unexpected prices response.");
        }
        result = result.concat(page);
        offset += 100;
    } while (page.length === 100);
    return result;
};

export const getBooking = async (id) => {
    if (!id) {
        throw new Error("No id specified");
    }
    const path = `/bookings/${id}`;
    const options = getOptions();
    const res = await request(path, options);
    const data = await res.json();
    if (data.statusCode >= 300) {
        throw new Error(data.message);
    }
    return data;
};

export const getContentBlocks = async (key, locationId = null) => {
    let path = `/content-blocks?key=${key}`;
    if (locationId != null) {
        path += `&location.id=${locationId}`;
    } else {
        path += "&location.id_null=1";
    }
    const options = getOptions();
    const res = await request(path, options);
    return await res.json();
};

export const createBooking = async (booking) => {
    const path = "/api/bookings";
    const res = await request(
        path,
        getOptions({
            method: "POST",
            body: JSON.stringify(booking),
        }),
    );
    if (!res.ok) {
        const text = await res.text();
        if (
            res.status === 400 &&
            text.toLowerCase().includes("one or more bookings at this time already exist")
        ) {
            throw new Error("conflict");
        }
        throw new Error(text);
    }
    return res.json();
};

export const deleteBooking = async (booking) => {
    const deleted_at = new Date().toISOString();
    const result = await updateBooking(booking.id, { deleted_at });
    return {
        ...booking,
        deleted_at: result.deleted_at,
    };
};

export const moveBooking = async (originalBooking, newDate) => {
    const path = `/bookings/${originalBooking.id}/move`;
    const res = await request(
        path,
        getOptions({
            method: "POST",
            body: JSON.stringify({ new_date: newDate }),
        }),
    );
    return {
        data: await res.json(),
        status: res.status,
    };

};

export const getTimeSlots = async (xcubeId, date, duration, bookingId) => {
    let path = `/bookings/available/slots?xcube=${xcubeId}&date=${date}&duration=${duration}`;
    if (bookingId) {
        path += `&bookingToIgnore=${bookingId}`;
    }
    const res = await request(
        path,
        getOptions({
            method: "GET",
        }),
    );
    return {
        json: await res.json(),
        status: res.status,
    };
};

export const activateApplicationVersion = async (applicationId, version, filter) => {
    const where = {};
    if (filter.location) {
        where.location_id = Number.parseInt(filter.location);
    }
    if (filter.type) {
        where.type = filter.type;
    }
    if (filter.status && filter.status === "active") {
        where.active = true;
    } else if (filter.status && filter.status === "inactive") {
        where.active = false;
    }
    if (filter.xcubeId) {
        where.xcube_id = Number.parseInt(filter.xcubeId);
    }
    if (filter.country) {
        where.country = filter.country;
    }
    if (filter.ignore_missing) {
        where.ignore_missing = true;
    }

    const path = `/applications/${applicationId}/versions/activate`;
    const res = await request(
        path,
        getOptions({
            method: "POST",
            body: JSON.stringify({
                ...where,
                version,
            }),
        }),
    );
    if (res.status !== 200) {
        throw new Error((await res.json()).message);
    }
};

export const deleteApplicationVersion = async (applicationId, version) => {
    const path = `/applications/${applicationId}/versions/delete`;
    const res = await request(
        path,
        getOptions({
            method: "POST",
            body: JSON.stringify({ version }),
        }),
    );
    if (res.status !== 200) {
        throw new Error((await res.json()).message);
    }
};


export const updateXCube = async (id, xCube) => {
    const path = `/xcubes/${id}`;
    const res = await request(
        path,
        getOptions({
            method: "PUT",
            body: JSON.stringify(xCube),
        }),
    );
    return {
        json: await res.json(),
        status: res.status,
    };
};

const getHeaders = () => {
    const token = getToken();

    const headers = {
        "Content-Type": "application/json",
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
};

const getOptions = (overwrites = {}) => {
    const options = Object.assign({}, defaultOptions);
    options.headers = getHeaders();
    return Object.assign({}, options, overwrites);
};

export const request = async (path, options = {}, data = undefined) => {
    if (data) options.body = JSON.stringify(data);
    const newOptions = getOptions(options);
    const response = await fetch(HOST + path, newOptions);
    if (response.status === 403 || response.status === 401) {
        logout();
        throw new Error("Invalid Token");
    }
    return response;
};

export const login = async (credentials) => {
    const path = "/auth/local";
    const options = getOptions({ method: "POST" });

    const result = await request(path, options, credentials);
    const json = await result.json();

    if (result.status !== 200) {
        throw new Error("Unknown username/password");
    }
    localStorage.setItem("xcube-session-key", json.jwt);
    await fetchUserData();
    return { status: result.status, role: json.user.role };
};

export const checkConfirmToken = async (token) => {
    const options = getOptions({ method: "GET" });
    const path = `/clients/isVerified?activation_token=${token}`;
    const response = await fetch(HOST + path, options);

    if (response.ok) {
        const { isVerified } = await response.json();
        return isVerified;
    }
    return false;
};

export const logout = async () => {
    localStorage.removeItem("xcube-session-key");
    const store = useCurrentUserStore();
    store.setCurrentUser(null);
    window.location.reload();
};

export const getApplicationRest = async (id) => {
    const path = `/applications/${id}`;
    const options = getOptions();
    const result = await request(path, options);
    return await result.json();
};

// const getBookings = async () => {
//   const path = "/bookings/next-bookings";
//   let options = getOptions();
//   const result = await request(path, options);
//   return result;
// };

export const updateBooking = async (id, booking) => {
    const path = `/bookings/${id}`;
    const res = await request(
        path,
        getOptions({
            method: "PUT",
            body: JSON.stringify(booking),
        }),
    );
    if (!res.ok) {
        const text = await res.text();
        throw new Error(text);
    }
    return await res.json();
};

export const updateClient = async (id, client) => {
    const path = `/clients/${id}`;
    const res = await request(
        path,
        getOptions({
            method: "PUT",
            body: JSON.stringify(client),
        }),
    );
    return await res.json();
};

export const updateTeam = async (id, team) => {
    const path = `/teams/${id}`;
    const res = await request(
        path,
        getOptions({
            method: "PUT",
            body: JSON.stringify(team),
        }),
    );
    return await res.json();
};

export const finishBooking = async (id) => {
    const path = `/bookings/${id}/finish`;
    const res = await request(
        path,
        getOptions({
            method: "PATCH",
            body: JSON.stringify({}),
        }),
    );
    return await res.json();
};

export const resendBookingMail = async (id, email) => {
    const path = `/bookings/${id}/send_invite/${email}`;
    const res = await request(
        path,
        getOptions({
            method: "POST",
            body: JSON.stringify({}),
        }),
    );
    return await res.json();
};

export const getCustomersCSVJSON = async () => {
    const path = "/export/customers";
    const res = await request(
        path,
        getOptions({
            method: "GET",
        }),
    );
    return res.json();
};

export const getCustomersCSV = async (filters) => {
    const params = filterToParams(filters);
    const path = `/bookings/export?${params ? queryString.stringify(params) : ""}`;
    const res = await request(
        path,
        getOptions({
            method: "GET",
            headers: {
                ...getHeaders(),
                accept: "text/csv",
            }
        }),
    );
    return res.text();
};

export const getInvoicingCSV = async (year, month) => {
    const path = `/bookings/invoicing/${year}/${month}`;
    const res = await request(
        path,
        getOptions({
            method: "GET",
            headers: {
                ...getHeaders(),
                accept: "text/csv",
            }
        }),
    );
    return res.text();
};

export const exchangeCodeForToken = async (code) => {
    const path = "/authorization-tokens/login";
    const body = JSON.stringify({ token: code });

    const result = await request(
        path,
        getOptions({
            method: "POST",
            body,
        }),
    );
    if (result.status === 410) {
        throw new Error("link.expired");
    }
    if (result.status !== 200) {
        throw new Error("server.error");
    }
    const json = await result.json();
    return json.jwt;
}

export const requestLoginLinkOnServer = async (email) => {
    const path = "/authorization-tokens/request";
    const body = JSON.stringify({ email, language: "en", system: "hub" });
    const result = await request(
        path,
        getOptions({
            method: "POST",
            body,
        }),
    );
    if (result.status !== 200) {
        throw new Error("Server error");
    }
};

export const getLocationUsers = async (locationId) => {
    const path = `/managed-users/location/${locationId}`;
    const options = getOptions();
    const res = await request(path, options);
    if (res.status !== 200) {
        throw new Error("Server error");
    }
    return await res.json();
};


export const unlinkUsersFromLocation = async (userId, locationId) => {
    const path = `/managed-users/${userId}/unlink`;
    const body = JSON.stringify({ location_id: locationId });
    const options = getOptions({
        method: "POST",
        body
    });
    const res = await request(path, options);
    if (res.status !== 200) {
        throw new Error("Server error");
    }
    return await res.json();
};

export const sendResetPasswordEmail = async (email) => {
    const res = await request(
        "/clients/forgot-password",
        { method: "POST" },
        {
            email: email,
        },
    );
    console.log("sendResetPasswordEmail response:", res);
    if (res.status !== 200) {
        throw new Error("Server error");
    }
    return;
};

export const sendInviteEmail = async (email) => {
    console.log("sendInviteEmail called with email:", email);
    const res = await request(
        "/managed-users/resend-invite",
        { method: "POST" },
        {
            email: email,
        },
    );
    if (res.status === 422) {
        const response = await res.json();
        throw new ValidationError(response.message || "Validation error");
    }
    if (res.status !== 200) {
        throw new Error("Server error");
    }
    return;
};

export const createManagedUser = async (email, username, location, role) => {
    const res = await request(
        "/managed-users",
        { method: "POST" },
        { email, username, location_id: location, role_type: role },
    );
    if (res.status === 422) {
        const response = await res.json();
        throw new ValidationError(response.message || "Validation error");
    }
    if (res.status !== 200) {
        throw new Error("Server error");
    }
    return await res.json();
};

export class ValidationError extends Error {}


export const confirmManagedUser = async (token, password) => {
    if (!token || !password) {
        throw new Error("Missing token or password");
    }
    const result = await request(
        "/managed-users/confirm",
        { method: "POST" },
        { token, password },
    );
    if (result.status === 422) {
        const response = await result.json();
        throw new ValidationError(response.message || "Validation error");
    }
    if (result.status !== 200) {
        throw new Error("Server error");
    }
    return await result.json();
};

const readSchedule = async (res) => {
    const body = await res.json().catch(() => ({}));
    if (res.status === 400 || res.status === 409) {
        throw new ValidationError(body.message || "Validation error");
    }
    if (!res.ok) {
        throw new Error(body.message || "Request failed");
    }
    return body;
};

export const getOpeningHours = (locationId) =>
    request(`/opening-hours/location/${locationId}`).then(readSchedule);

export const replaceOpeningHours = (locationId, body) =>
    request(`/opening-hours/location/${locationId}`, { method: "PUT" }, body).then(readSchedule);

export const getClosures = (locationId) =>
    request(`/temporarily-unavailables/location/${locationId}`).then(readSchedule);

export const createClosure = (locationId, body) =>
    request(`/temporarily-unavailables/location/${locationId}`, { method: "POST" }, body).then(
        readSchedule,
    );

export const deleteClosure = (locationId, closureId) =>
    request(`/temporarily-unavailables/location/${locationId}/${closureId}`, {
        method: "DELETE",
    }).then(readSchedule);

export const getOpeningHourOverrides = (locationId) =>
    request(`/opening-hour-overrides/location/${locationId}`).then(readSchedule);

export const createOpeningHourOverride = (locationId, body) =>
    request(`/opening-hour-overrides/location/${locationId}`, { method: "POST" }, body).then(
        readSchedule,
    );

export const deleteOpeningHourOverride = (locationId, overrideId) =>
    request(`/opening-hour-overrides/location/${locationId}/${overrideId}`, {
        method: "DELETE",
    }).then(readSchedule);

export const getLocationSchedule = (locationId) =>
    Promise.all([
        getOpeningHours(locationId),
        getOpeningHourOverrides(locationId),
        getClosures(locationId),
    ]).then(([rows, overrides, closures]) => ({ rows, overrides, closures }));

